import { useEffect, useMemo, useRef, useState } from "react";
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  Loader2,
  MapPin,
  RefreshCw,
  Search,
  Store,
  X,
} from "lucide-react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useStores } from "../../hooks/useStores";
import {
  getStoreProductionHistory,
  STORE_PRODUCTION_RETAILERS,
  STORE_PRODUCTION_RETAILER_LABELS,
  type StoreProductionHistory,
  type StoreProductionRetailer,
} from "../../services/StoreProductionHistoryService";
import {
  buildAdminStoreOptions,
  type AdminStoreOption,
} from "../AdminStorePicker.utils";
import {
  buildAggregateStoreProduction,
  buildStoreProductionSummaries,
  classifyStoreProduction,
  median,
  type StoreProductionStatus,
  type StoreProductionSummary,
} from "./StoreProductionHistoryPanel.utils";

type UserStoreMap = Partial<Record<StoreProductionRetailer, string[]>>;
const EMPTY_USER_STORE_MAP: UserStoreMap = {};

interface StoreProductionHistoryPanelProps {
  mode: "user" | "store";
  storeIdsByRetailer?: UserStoreMap;
  /** In user mode, also reports the user's unique products (merged across stores). */
  userId?: string;
  historyLoader?: typeof getStoreProductionHistory;
  catalogStoresOverride?: unknown[];
}

const RANGE_OPTIONS = [7, 14, 30] as const;
const SEARCH_RESULT_LIMIT = 8;

const STATUS_COPY: Record<StoreProductionStatus, { label: string; classes: string }> = {
  normal: {
    label: "Healthy",
    classes: "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300 dark:ring-emerald-800",
  },
  low: {
    label: "Low",
    classes: "bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:ring-amber-800",
  },
  issue: {
    label: "Issue",
    classes: "bg-red-50 text-red-700 ring-red-200 dark:bg-red-900/20 dark:text-red-300 dark:ring-red-800",
  },
  missing: {
    label: "Missing",
    classes: "bg-red-50 text-red-700 ring-red-200 dark:bg-red-900/20 dark:text-red-300 dark:ring-red-800",
  },
  no_baseline: {
    label: "New",
    classes: "bg-blue-50 text-blue-700 ring-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:ring-blue-800",
  },
};

function formatDate(date: string, includeYear = false) {
  return new Date(`${date}T12:00:00`).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    ...(includeYear ? { year: "numeric" } : {}),
  });
}

function formatCompact(value: number) {
  return new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value);
}

function formatDelta(delta: number | null) {
  if (delta === null) return "—";
  return `${delta > 0 ? "+" : ""}${Math.round(delta)}%`;
}

function statusTone(status: StoreProductionStatus) {
  if (status === "issue" || status === "missing") return "red";
  if (status === "low") return "amber";
  if (status === "normal") return "emerald";
  return "blue";
}

function StatusBadge({ status }: { status: StoreProductionStatus }) {
  const copy = STATUS_COPY[status];
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-[10px] font-bold ring-1 ring-inset ${copy.classes}`}>
      {copy.label}
    </span>
  );
}

function Sparkline({ summary }: { summary: StoreProductionSummary }) {
  const width = 100;
  const height = 28;
  const points = summary.values
    .map((value, index) => ({ value, index }))
    .filter((point): point is { value: number; index: number } => point.value !== null);
  if (points.length < 2) return <span className="text-gray-300 dark:text-dark-500">—</span>;
  const min = Math.min(...points.map((point) => point.value));
  const max = Math.max(...points.map((point) => point.value));
  const spread = Math.max(1, max - min);
  const lastIndex = Math.max(1, summary.values.length - 1);
  const polyline = points.map((point) => {
    const x = (point.index / lastIndex) * (width - 4) + 2;
    const y = height - 3 - ((point.value - min) / spread) * (height - 6);
    return `${x},${y}`;
  }).join(" ");
  const color = summary.status === "issue" || summary.status === "missing"
    ? "text-red-500"
    : summary.status === "low"
      ? "text-amber-500"
      : "text-blue-500";
  return (
    <svg viewBox={`0 0 ${width} ${height}`} className={`h-7 w-24 ${color}`} aria-label={`${summary.storeId} production trend`}>
      <polyline points={polyline} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function StoreSummaryTable({ summaries }: { summaries: StoreProductionSummary[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full">
        <thead>
          <tr className="border-b border-gray-100 bg-gray-50/60 dark:border-dark-700 dark:bg-dark-900/20">
            {[
              ["Store", "text-left"],
              ["Latest", "text-right"],
              ["Normal", "text-right"],
              ["Change", "text-right"],
              ["Trend", "text-center"],
              ["Categories", "text-right"],
              ["Status", "text-right"],
            ].map(([label, align]) => (
              <th key={label} className={`px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.12em] text-gray-400 dark:text-dark-400 ${align}`}>
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {summaries.map((summary) => (
            <tr key={summary.storeId} className="border-b border-gray-50 last:border-0 dark:border-dark-700/60">
              <td className="px-4 py-2.5 text-xs font-mono font-semibold text-blue-600 dark:text-blue-400">#{summary.storeId}</td>
              <td className="px-4 py-2.5 text-right text-xs font-mono tabular-nums text-gray-900 dark:text-white">
                {summary.latest === null ? "—" : summary.latest.toLocaleString()}
              </td>
              <td className="px-4 py-2.5 text-right text-xs font-mono tabular-nums text-gray-500 dark:text-dark-300">
                {summary.baseline === null ? "—" : Math.round(summary.baseline).toLocaleString()}
              </td>
              <td className={`px-4 py-2.5 text-right text-xs font-mono tabular-nums ${
                summary.deltaPercent !== null && summary.deltaPercent < -25
                  ? "font-bold text-red-600 dark:text-red-400"
                  : "text-gray-500 dark:text-dark-300"
              }`}>
                {formatDelta(summary.deltaPercent)}
              </td>
              <td className="px-4 py-1 text-center"><Sparkline summary={summary} /></td>
              <td className="px-4 py-2.5 text-right text-xs font-mono tabular-nums text-gray-500 dark:text-dark-300">
                {summary.categoriesFound === null
                  ? "—"
                  : `${summary.categoriesFound}/${summary.categoriesExpected || "—"}`}
              </td>
              <td className="px-4 py-2.5 text-right"><StatusBadge status={summary.status} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PanelEmptyState({ message }: { message: string }) {
  return (
    <div className="flex min-h-52 flex-col items-center justify-center rounded-2xl bg-white px-6 text-center ring-1 ring-gray-200 dark:bg-dark-800 dark:ring-dark-700">
      <Store className="mb-2 h-6 w-6 text-gray-300 dark:text-dark-500" />
      <p className="text-sm font-semibold text-gray-700 dark:text-dark-100">{message}</p>
    </div>
  );
}

function SearchResultStatus({ summary }: { summary?: StoreProductionSummary }) {
  if (!summary) return null;
  const dot = statusTone(summary.status);
  const dotClass = dot === "red"
    ? "bg-red-500"
    : dot === "amber"
      ? "bg-amber-500"
      : dot === "emerald"
        ? "bg-emerald-500"
        : "bg-blue-500";
  return (
    <span className="ml-auto flex shrink-0 items-center gap-2 pl-4 text-[11px] font-semibold text-gray-500 dark:text-dark-300">
      <span className={`h-2 w-2 rounded-full ${dotClass}`} />
      {formatDelta(summary.deltaPercent)}
    </span>
  );
}

function StoreSearchBox({
  options,
  selectableIds,
  summaries,
  selectedStoreId,
  onSelect,
  includeAll,
  loading,
}: {
  options: AdminStoreOption[];
  selectableIds: string[];
  summaries: StoreProductionSummary[];
  selectedStoreId: string;
  onSelect: (storeId: string) => void;
  includeAll: boolean;
  loading: boolean;
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const blurTimer = useRef<number | undefined>(undefined);
  const selectable = useMemo(() => new Set(selectableIds), [selectableIds]);
  const summaryById = useMemo(
    () => new Map(summaries.map((summary) => [summary.storeId, summary])),
    [summaries],
  );
  const availableOptions = useMemo(() => {
    const known = options.filter((option) => selectable.has(option.normalizedId));
    const knownIds = new Set(known.map((option) => option.normalizedId));
    return [
      ...known,
      ...selectableIds
        .filter((storeId) => !knownIds.has(storeId))
        .map((storeId) => ({
          normalizedId: storeId,
          normalizedAddress: "Address unavailable",
          normalizedPostalCode: "",
        })),
    ];
  }, [options, selectable, selectableIds]);
  const selectedOption = availableOptions.find((option) => option.normalizedId === selectedStoreId);
  const normalizedQuery = query.trim().toLowerCase();
  const filtered = availableOptions.filter((option) => {
    if (!normalizedQuery) return true;
    return option.normalizedId.toLowerCase().includes(normalizedQuery)
      || option.normalizedAddress.toLowerCase().includes(normalizedQuery)
      || option.normalizedPostalCode.toLowerCase().includes(normalizedQuery);
  }).slice(0, SEARCH_RESULT_LIMIT);
  const displayValue = open
    ? query
    : selectedStoreId
      ? `#${selectedStoreId}${selectedOption?.normalizedAddress && selectedOption.normalizedAddress !== "Address unavailable" ? ` · ${selectedOption.normalizedAddress}` : ""}`
      : includeAll
        ? `All ${selectableIds.length.toLocaleString()} stores`
        : "";

  const select = (storeId: string) => {
    onSelect(storeId);
    setQuery("");
    setOpen(false);
  };

  useEffect(() => () => {
    if (blurTimer.current !== undefined) window.clearTimeout(blurTimer.current);
  }, []);

  return (
    <div className="relative min-w-0 flex-1">
      <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.14em] text-gray-400 dark:text-dark-400">
        Store search
      </label>
      <div className={`relative flex h-11 items-center rounded-xl bg-white ring-1 transition-shadow dark:bg-dark-800 ${open ? "ring-2 ring-blue-500" : "ring-gray-200 dark:ring-dark-600"}`}>
        <Search className="pointer-events-none absolute left-3.5 h-4 w-4 text-gray-400 dark:text-dark-400" />
        <input
          role="combobox"
          aria-label="Search stores"
          aria-expanded={open}
          aria-controls="production-store-results"
          autoComplete="off"
          value={displayValue}
          placeholder="Search store ID, city, ZIP, or address"
          disabled={loading || selectableIds.length === 0}
          onFocus={() => {
            if (blurTimer.current !== undefined) window.clearTimeout(blurTimer.current);
            setQuery("");
            setOpen(true);
          }}
          onBlur={() => {
            blurTimer.current = window.setTimeout(() => setOpen(false), 120);
          }}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onKeyDown={(event) => {
            if (event.key === "Escape") setOpen(false);
            if (event.key === "Enter") {
              event.preventDefault();
              if (filtered[0]) select(filtered[0].normalizedId);
            }
          }}
          className="h-full w-full rounded-xl bg-transparent pl-10 pr-20 text-sm font-medium text-gray-900 outline-none placeholder:text-gray-400 disabled:cursor-not-allowed disabled:opacity-60 dark:text-white dark:placeholder:text-dark-400"
        />
        {open && query ? (
          <button
            type="button"
            aria-label="Clear store search"
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => setQuery("")}
            className="absolute right-3 rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-dark-700 dark:hover:text-white"
          >
            <X className="h-4 w-4" />
          </button>
        ) : (
          <ChevronDown className="pointer-events-none absolute right-3 h-4 w-4 text-gray-400 dark:text-dark-400" />
        )}
      </div>

      {open && (
        <div
          id="production-store-results"
          role="listbox"
          className="absolute z-30 mt-2 max-h-80 w-full overflow-y-auto rounded-xl bg-white p-1.5 shadow-xl ring-1 ring-gray-200 dark:bg-dark-800 dark:ring-dark-600"
        >
          {includeAll && !normalizedQuery && (
            <button
              type="button"
              role="option"
              aria-selected={!selectedStoreId}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => select("")}
              className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left ${!selectedStoreId ? "bg-blue-50 dark:bg-blue-900/20" : "hover:bg-gray-50 dark:hover:bg-dark-700"}`}
            >
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300">
                <Store className="h-4 w-4" />
              </span>
              <span>
                <span className="block text-xs font-bold text-gray-900 dark:text-white">All user stores</span>
                <span className="block text-[11px] text-gray-500 dark:text-dark-300">Combined production for {selectableIds.length.toLocaleString()} stores</span>
              </span>
            </button>
          )}
          {filtered.length > 0 ? filtered.map((option) => (
            <button
              key={option.normalizedId}
              type="button"
              role="option"
              aria-selected={option.normalizedId === selectedStoreId}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => select(option.normalizedId)}
              className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left ${option.normalizedId === selectedStoreId ? "bg-blue-50 dark:bg-blue-900/20" : "hover:bg-gray-50 dark:hover:bg-dark-700"}`}
            >
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-500 dark:bg-dark-700 dark:text-dark-200">
                <MapPin className="h-4 w-4" />
              </span>
              <span className="min-w-0">
                <span className="block text-xs font-bold text-gray-900 dark:text-white">Store #{option.normalizedId}</span>
                <span className="block truncate text-[11px] text-gray-500 dark:text-dark-300">
                  {option.normalizedAddress}{option.normalizedPostalCode ? ` · ${option.normalizedPostalCode}` : ""}
                </span>
              </span>
              <SearchResultStatus summary={summaryById.get(option.normalizedId)} />
            </button>
          )) : (
            <div className="px-4 py-7 text-center">
              <p className="text-xs font-semibold text-gray-700 dark:text-dark-100">No matching stores</p>
              <p className="mt-1 text-[11px] text-gray-400 dark:text-dark-400">Try a store number, city, address, or ZIP code.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function StoreProductionHistoryPanel({
  mode,
  storeIdsByRetailer,
  userId,
  historyLoader = getStoreProductionHistory,
  catalogStoresOverride,
}: StoreProductionHistoryPanelProps) {
  const storeMap = storeIdsByRetailer || EMPTY_USER_STORE_MAP;
  const userRetailers = useMemo(
    () => STORE_PRODUCTION_RETAILERS.filter((retailer) => (storeMap[retailer]?.length || 0) > 0),
    [storeMap],
  );
  const retailerOptions = useMemo(
    () => mode === "user" ? userRetailers : [...STORE_PRODUCTION_RETAILERS],
    [mode, userRetailers],
  );
  const defaultRetailer = retailerOptions.includes("walmart") ? "walmart" : retailerOptions[0] || "walmart";
  const [retailer, setRetailer] = useState<StoreProductionRetailer>(defaultRetailer);
  const [days, setDays] = useState<(typeof RANGE_OPTIONS)[number]>(7);
  const [selectedStoreId, setSelectedStoreId] = useState("");
  const [history, setHistory] = useState<StoreProductionHistory | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const { stores: loadedCatalogStores, loading: loadedCatalogLoading } = useStores(retailer);
  const catalogStores = catalogStoresOverride || loadedCatalogStores;
  const catalogLoading = catalogStoresOverride ? false : loadedCatalogLoading;

  useEffect(() => {
    if (!retailerOptions.includes(retailer)) setRetailer(defaultRetailer);
  }, [defaultRetailer, retailer, retailerOptions]);

  useEffect(() => {
    setSelectedStoreId("");
  }, [retailer]);

  const userStoreIds = useMemo(() => storeMap[retailer] || [], [retailer, storeMap]);
  const requestStoreIds = useMemo(
    () => mode === "user"
      ? selectedStoreId ? [selectedStoreId] : userStoreIds
      : selectedStoreId ? [selectedStoreId] : [],
    [mode, selectedStoreId, userStoreIds],
  );

  useEffect(() => {
    if (mode === "user" && requestStoreIds.length === 0) {
      setHistory(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError("");
    historyLoader({ retailer, days, storeIds: requestStoreIds, userId: mode === "user" ? userId : undefined })
      .then((data) => {
        if (cancelled) return;
        setHistory(data);
        if (mode === "store" && !selectedStoreId && data.availableStoreIds.length > 0) {
          setSelectedStoreId(data.availableStoreIds[0]);
        }
      })
      .catch((cause) => {
        if (!cancelled) {
          setHistory(null);
          setError(cause instanceof Error ? cause.message : "Failed to load production history");
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days, historyLoader, mode, refreshKey, requestStoreIds, retailer, selectedStoreId, userId]);

  const activeStoreIds = useMemo(() => {
    if (!history) return [];
    if (mode === "user") return selectedStoreId ? [selectedStoreId] : userStoreIds;
    return selectedStoreId ? [selectedStoreId] : [];
  }, [history, mode, selectedStoreId, userStoreIds]);
  const selectableIds = useMemo(
    () => mode === "user" ? userStoreIds : history?.availableStoreIds || [],
    [history?.availableStoreIds, mode, userStoreIds],
  );
  const catalogOptions = useMemo(
    () => buildAdminStoreOptions(catalogStores as unknown[] | null, retailer),
    [catalogStores, retailer],
  );
  const selectedOption = useMemo(
    () => catalogOptions.find((option) => option.normalizedId === selectedStoreId),
    [catalogOptions, selectedStoreId],
  );
  const chartData = useMemo(
    () => history
      ? buildAggregateStoreProduction(history, activeStoreIds, mode === "user" && !selectedStoreId)
        .map((point) => ({ ...point, label: formatDate(point.date) }))
      : [],
    [activeStoreIds, history, mode, selectedStoreId],
  );
  const summaries = useMemo(
    () => history ? buildStoreProductionSummaries(history, activeStoreIds) : [],
    [activeStoreIds, history],
  );
  const latestPoint = [...chartData].reverse().find((point) => point.products !== null);
  const latestValue = latestPoint?.products ?? null;
  const latestUniqueValue = latestPoint?.uniqueProducts ?? null;
  const showUniqueLine = activeStoreIds.length > 1 && chartData.some((point) => point.uniqueProducts !== null);
  const baselineValues = chartData
    .filter((point) => point.date !== latestPoint?.date)
    .map((point) => point.products)
    .filter((value): value is number => value !== null && value > 0);
  const aggregateBaseline = median(baselineValues);
  const aggregateDelta = latestValue !== null && aggregateBaseline !== null && aggregateBaseline > 0
    ? ((latestValue - aggregateBaseline) / aggregateBaseline) * 100
    : null;
  const storesToReview = summaries.filter((summary) => summary.status === "issue" || summary.status === "missing" || summary.status === "low");
  const aggregateStatus: StoreProductionStatus = storesToReview.some((summary) => summary.status === "issue" || summary.status === "missing")
    ? "issue"
    : storesToReview.some((summary) => summary.status === "low")
      ? "low"
      : aggregateBaseline === null
        ? "no_baseline"
        : "normal";
  const latestSnapshot = history ? [...history.snapshots].reverse().find((snapshot) => snapshot.available) : undefined;
  const latestStoreRows = latestSnapshot?.stores.filter((store) => activeStoreIds.includes(store.storeId)) || [];
  const categoriesFound = latestStoreRows.reduce((sum, row) => sum + row.categoriesFound, 0);
  const categoriesExpected = latestStoreRows.reduce((sum, row) => sum + row.categoriesExpected, 0);
  const storesReported = latestPoint?.storesReported || 0;
  const storesExpected = latestPoint?.storesExpected || activeStoreIds.length;
  const absoluteDifference = latestValue !== null && aggregateBaseline !== null
    ? latestValue - aggregateBaseline
    : null;
  const normalLow = aggregateBaseline === null ? null : aggregateBaseline * 0.85;
  const normalHigh = aggregateBaseline === null ? null : aggregateBaseline * 1.15;
  const chartNumbers = chartData
    .flatMap((point) => showUniqueLine ? [point.products, point.uniqueProducts] : [point.products])
    .filter((value): value is number => value !== null);
  if (normalLow !== null) chartNumbers.push(normalLow);
  if (normalHigh !== null) chartNumbers.push(normalHigh);
  const chartMinValue = chartNumbers.length > 0 ? Math.min(...chartNumbers) : 0;
  const chartMaxValue = chartNumbers.length > 0 ? Math.max(...chartNumbers) : 1;
  const chartPadding = Math.max(20, (chartMaxValue - chartMinValue) * 0.24, chartMaxValue * 0.04);
  const chartDomain: [number, number] = [
    Math.max(0, Math.floor(chartMinValue - chartPadding)),
    Math.ceil(chartMaxValue + chartPadding),
  ];
  const historyRows = useMemo(() => {
    if (!history) return [];
    return [...history.snapshots].reverse().map((snapshot) => {
      const rows = snapshot.stores.filter((store) => activeStoreIds.includes(store.storeId));
      const point = chartData.find((candidate) => candidate.date === snapshot.date);
      const production = point?.products ?? null;
      return {
        date: snapshot.date,
        production,
        uniqueProducts: point?.uniqueProducts ?? null,
        storesReported: point?.storesReported || 0,
        storesExpected: point?.storesExpected || activeStoreIds.length,
        categoriesFound: rows.reduce((sum, row) => sum + row.categoriesFound, 0),
        categoriesExpected: rows.reduce((sum, row) => sum + row.categoriesExpected, 0),
        status: snapshot.available
          ? classifyStoreProduction(production, aggregateBaseline)
          : "missing" as StoreProductionStatus,
      };
    });
  }, [activeStoreIds, aggregateBaseline, chartData, history]);

  if (mode === "user" && retailerOptions.length === 0) {
    return <PanelEmptyState message="This user has no stores with production history." />;
  }

  const statusHeadline = aggregateStatus === "normal"
    ? "Production is within the normal range"
    : aggregateStatus === "low"
      ? "Production is lower than usual"
      : aggregateStatus === "issue"
        ? "Production needs review"
        : "Building a production baseline";
  const deltaCopy = aggregateDelta === null
    ? "There is not enough prior data to calculate a reliable comparison yet."
    : `${Math.abs(Math.round(aggregateDelta))}% ${aggregateDelta >= 0 ? "above" : "below"} the ${days}-day median.`;
  const insightClasses = aggregateStatus === "normal"
    ? "bg-emerald-50 text-emerald-900 ring-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-100 dark:ring-emerald-800"
    : aggregateStatus === "low"
      ? "bg-amber-50 text-amber-900 ring-amber-200 dark:bg-amber-900/20 dark:text-amber-100 dark:ring-amber-800"
      : aggregateStatus === "issue"
        ? "bg-red-50 text-red-900 ring-red-200 dark:bg-red-900/20 dark:text-red-100 dark:ring-red-800"
        : "bg-blue-50 text-blue-900 ring-blue-200 dark:bg-blue-900/20 dark:text-blue-100 dark:ring-blue-800";

  return (
    <div className="space-y-4">
      <section className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-gray-200 dark:bg-dark-800 dark:ring-dark-700 sm:p-5">
        <div className="grid gap-5 xl:grid-cols-[260px_minmax(320px,1fr)_400px] xl:items-start">
          <div className="min-w-0">
            <div className="flex items-center gap-2.5">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 text-blue-600 dark:bg-blue-900/25 dark:text-blue-300">
                <Activity className="h-4 w-4" />
              </span>
              <h2 className="text-lg font-bold tracking-tight text-gray-950 dark:text-white">Store production</h2>
            </div>
            <p className="mt-2 text-xs leading-relaxed text-gray-500 dark:text-dark-300">
              Search for a store to view daily production and compare it with normal.
            </p>
          </div>

          <StoreSearchBox
            options={catalogOptions}
            selectableIds={selectableIds}
            summaries={summaries}
            selectedStoreId={selectedStoreId}
            onSelect={setSelectedStoreId}
            includeAll={mode === "user"}
            loading={catalogLoading || (loading && selectableIds.length === 0)}
          />

          <div>
            <label className="block">
              <span className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.14em] text-gray-400 dark:text-dark-400">Retailer</span>
              <span className="relative block">
                <select
                  aria-label="Production retailer"
                  value={retailer}
                  onChange={(event) => setRetailer(event.target.value as StoreProductionRetailer)}
                  className="h-11 w-full appearance-none rounded-xl bg-gray-50 px-3 pr-9 text-sm font-semibold text-gray-900 ring-1 ring-gray-200 outline-none focus:ring-2 focus:ring-blue-500 dark:bg-dark-700 dark:text-white dark:ring-dark-600"
                >
                  {retailerOptions.map((option) => <option key={option} value={option}>{STORE_PRODUCTION_RETAILER_LABELS[option]}</option>)}
                </select>
                <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              </span>
            </label>
            <div className="mt-3 flex min-w-0 flex-wrap items-center justify-end gap-3">
              <div className="inline-flex min-w-0 flex-[1_1_250px] overflow-hidden rounded-lg bg-gray-100 p-1 dark:bg-dark-700" aria-label="Production history range">
                {RANGE_OPTIONS.map((range) => (
                  <button
                    key={range}
                    type="button"
                    onClick={() => setDays(range)}
                    aria-pressed={days === range}
                    className={`min-w-0 flex-1 whitespace-nowrap rounded-md px-1.5 py-1.5 text-xs font-semibold transition ${days === range ? "bg-blue-600 text-white shadow-sm dark:bg-blue-500" : "text-gray-500 hover:text-gray-900 dark:text-dark-300 dark:hover:text-white"}`}
                  >
                    {range} days
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setRefreshKey((value) => value + 1)}
                disabled={loading}
                className="ml-auto flex h-9 shrink-0 items-center gap-2 rounded-lg px-3 text-xs font-semibold text-gray-600 ring-1 ring-gray-200 hover:bg-gray-50 disabled:opacity-50 dark:text-dark-200 dark:ring-dark-600 dark:hover:bg-dark-700"
              >
                {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                Refresh
              </button>
            </div>
          </div>
        </div>
      </section>

      {error ? (
        <div role="alert" className="rounded-2xl bg-red-50 p-4 text-sm text-red-700 ring-1 ring-red-200 dark:bg-red-900/20 dark:text-red-300 dark:ring-red-800">
          {error}
        </div>
      ) : loading && !history ? (
        <div className="flex h-64 items-center justify-center rounded-2xl bg-white ring-1 ring-gray-200 dark:bg-dark-800 dark:ring-dark-700">
          <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
        </div>
      ) : history && activeStoreIds.length > 0 ? (
        <>
          <section className="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-gray-200 dark:bg-dark-800 dark:ring-dark-700">
            <div className="flex flex-col gap-3 border-b border-gray-100 px-4 py-4 dark:border-dark-700 sm:flex-row sm:items-center sm:justify-between sm:px-5">
              <div className="flex min-w-0 items-center gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600 dark:bg-blue-900/25 dark:text-blue-300">
                  <Store className="h-5 w-5" />
                </span>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-sm font-bold text-gray-950 dark:text-white">
                      {selectedStoreId ? `${STORE_PRODUCTION_RETAILER_LABELS[retailer]} #${selectedStoreId}` : `All ${activeStoreIds.length.toLocaleString()} user stores`}
                    </h3>
                    <StatusBadge status={aggregateStatus} />
                  </div>
                  <p className="mt-0.5 truncate text-xs text-gray-500 dark:text-dark-300">
                    {selectedStoreId
                      ? selectedOption?.normalizedAddress || `${STORE_PRODUCTION_RETAILER_LABELS[retailer]} location`
                      : `${STORE_PRODUCTION_RETAILER_LABELS[retailer]} · combined production`}
                  </p>
                </div>
              </div>
              <p className="text-[11px] text-gray-400 dark:text-dark-400">
                Latest snapshot {latestSnapshot ? formatDate(latestSnapshot.date, true) : "unavailable"}
              </p>
            </div>

            <div className="grid divide-y divide-gray-100 dark:divide-dark-700 sm:grid-cols-2 sm:divide-x sm:divide-y-0 lg:grid-cols-5">
              <div className="px-4 py-3.5 sm:px-5">
                <p className="text-[10px] font-bold uppercase tracking-[0.13em] text-gray-400 dark:text-dark-400">Today</p>
                <p className="mt-1 text-xl font-bold tabular-nums text-gray-950 dark:text-white">{latestValue === null ? "—" : latestValue.toLocaleString()}</p>
                <p className="mt-0.5 text-[11px] text-gray-500 dark:text-dark-300">
                  {activeStoreIds.length > 1 && latestUniqueValue !== null
                    ? `Store rows · ${latestUniqueValue.toLocaleString()} unique products`
                    : "Products observed"}
                </p>
              </div>
              <div className="px-4 py-3.5 sm:px-5">
                <p className="text-[10px] font-bold uppercase tracking-[0.13em] text-gray-400 dark:text-dark-400">Normal</p>
                <p className="mt-1 text-xl font-bold tabular-nums text-gray-950 dark:text-white">{aggregateBaseline === null ? "—" : Math.round(aggregateBaseline).toLocaleString()}</p>
                <p className="mt-0.5 text-[11px] text-gray-500 dark:text-dark-300">Prior-day median</p>
              </div>
              <div className="px-4 py-3.5 sm:px-5">
                <p className="text-[10px] font-bold uppercase tracking-[0.13em] text-gray-400 dark:text-dark-400">Difference</p>
                <p className={`mt-1 text-xl font-bold tabular-nums ${absoluteDifference !== null && absoluteDifference < 0 ? "text-red-600 dark:text-red-400" : "text-emerald-600 dark:text-emerald-400"}`}>
                  {absoluteDifference === null ? "—" : `${absoluteDifference > 0 ? "+" : ""}${Math.round(absoluteDifference).toLocaleString()}`}
                </p>
                <p className="mt-0.5 text-[11px] text-gray-500 dark:text-dark-300">{formatDelta(aggregateDelta)} vs normal</p>
              </div>
              <div className="px-4 py-3.5 sm:px-5">
                <p className="text-[10px] font-bold uppercase tracking-[0.13em] text-gray-400 dark:text-dark-400">Categories</p>
                <p className="mt-1 text-xl font-bold tabular-nums text-gray-950 dark:text-white">{categoriesExpected > 0 ? `${categoriesFound}/${categoriesExpected}` : "—"}</p>
                <p className="mt-0.5 text-[11px] text-gray-500 dark:text-dark-300">Found / expected</p>
              </div>
              <div className="px-4 py-3.5 sm:px-5">
                <p className="text-[10px] font-bold uppercase tracking-[0.13em] text-gray-400 dark:text-dark-400">Coverage</p>
                <p className="mt-1 text-xl font-bold tabular-nums text-gray-950 dark:text-white">{storesReported}/{storesExpected}</p>
                <p className="mt-0.5 text-[11px] text-gray-500 dark:text-dark-300">Stores reported</p>
              </div>
            </div>
          </section>

          <section className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-gray-200 dark:bg-dark-800 dark:ring-dark-700 sm:p-5">
            <div className="mb-3 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <h3 className="text-sm font-bold text-gray-950 dark:text-white">Daily production</h3>
                <p className="mt-0.5 text-[11px] text-gray-500 dark:text-dark-300">
                  Products updated across {activeStoreIds.length === 1 ? "this store" : `${activeStoreIds.length.toLocaleString()} selected stores`}
                </p>
                {aggregateBaseline !== null && (
                  <div className="mt-3 flex flex-wrap items-center gap-4 text-[10px] font-medium text-gray-500 dark:text-dark-300">
                    <span className="flex items-center gap-1.5"><span className="h-2 w-5 rounded-sm bg-blue-100 dark:bg-blue-900/40" /> Normal range (±15%)</span>
                    <span className="flex items-center gap-1.5"><span className="w-5 border-t border-dashed border-gray-400" /> Median</span>
                    {showUniqueLine && <span className="flex items-center gap-1.5"><span className="w-5 border-t-2 border-emerald-500" /> Unique products (as on the products page)</span>}
                  </div>
                )}
              </div>
              <div className={`flex items-start gap-2.5 rounded-xl px-3.5 py-3 ring-1 ${insightClasses}`}>
                {aggregateStatus === "normal" ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" /> : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />}
                <p className="text-xs leading-relaxed">
                  <span className="font-bold">{statusHeadline}</span> — {deltaCopy}
                  {storesToReview.length > 0 && activeStoreIds.length > 1 ? ` ${storesToReview.length} ${storesToReview.length === 1 ? "store is" : "stores are"} below its usual level.` : ""}
                </p>
              </div>
            </div>
            <div className="h-72 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 12, right: 12, left: 0, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke="rgba(148, 163, 184, 0.18)" />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} minTickGap={24} />
                  <YAxis domain={chartDomain} tickFormatter={formatCompact} tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} width={48} allowDecimals={false} />
                  <Tooltip
                    formatter={(value, name) => [`${Number(value).toLocaleString()} products`, name]}
                    labelFormatter={(label) => `Snapshot ${label}`}
                    contentStyle={{ borderRadius: 10, border: "1px solid #e5e7eb", fontSize: 12 }}
                  />
                  {normalLow !== null && normalHigh !== null && (
                    <ReferenceArea y1={normalLow} y2={normalHigh} fill="#dbeafe" fillOpacity={0.55} strokeOpacity={0} />
                  )}
                  {aggregateBaseline !== null && <ReferenceLine y={aggregateBaseline} stroke="#94a3b8" strokeDasharray="5 5" />}
                  <Line
                    type="monotone"
                    dataKey="products"
                    name="Production"
                    stroke="#2563eb"
                    strokeWidth={2.5}
                    connectNulls={false}
                    isAnimationActive={false}
                    dot={chartData.length <= 14 ? { fill: "#2563eb", strokeWidth: 0, r: 3 } : false}
                    activeDot={{ fill: "#2563eb", stroke: "#fff", strokeWidth: 2, r: 5 }}
                  />
                  {showUniqueLine && (
                    <Line
                      type="monotone"
                      dataKey="uniqueProducts"
                      name="Unique products"
                      stroke="#10b981"
                      strokeWidth={2}
                      connectNulls={false}
                      isAnimationActive={false}
                      dot={chartData.length <= 14 ? { fill: "#10b981", strokeWidth: 0, r: 2.5 } : false}
                      activeDot={{ fill: "#10b981", stroke: "#fff", strokeWidth: 2, r: 4 }}
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </div>

          </section>

          <section className="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-gray-200 dark:bg-dark-800 dark:ring-dark-700">
            <div className="border-b border-gray-100 px-4 py-3.5 dark:border-dark-700 sm:px-5">
              <h3 className="text-sm font-bold text-gray-950 dark:text-white">Daily history</h3>
              <p className="mt-0.5 text-[11px] text-gray-500 dark:text-dark-300">A day-by-day view of production, category completion, and snapshot coverage.</p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/60 dark:border-dark-700 dark:bg-dark-900/20">
                    {[
                      ["Date", "text-left"],
                      ["Production", "text-right"],
                      ["Unique", "text-right"],
                      ["Normal", "text-right"],
                      ["Change", "text-right"],
                      ["Categories", "text-right"],
                      ["Coverage", "text-right"],
                      ["Status", "text-right"],
                    ].map(([label, align]) => (
                      <th key={label} className={`whitespace-nowrap px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.12em] text-gray-400 dark:text-dark-400 sm:px-5 ${align}`}>{label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {historyRows.map((row) => {
                    const rowDelta = row.production !== null && aggregateBaseline !== null && aggregateBaseline > 0
                      ? ((row.production - aggregateBaseline) / aggregateBaseline) * 100
                      : null;
                    return (
                      <tr key={row.date} className="border-b border-gray-50 last:border-0 dark:border-dark-700/60">
                        <td className="whitespace-nowrap px-4 py-3 text-xs font-semibold text-gray-900 dark:text-white sm:px-5">{formatDate(row.date, true)}</td>
                        <td className="px-4 py-3 text-right text-xs font-mono tabular-nums text-gray-900 dark:text-white sm:px-5">{row.production === null ? "—" : row.production.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right text-xs font-mono tabular-nums text-emerald-700 dark:text-emerald-300 sm:px-5">{row.uniqueProducts === null ? "—" : row.uniqueProducts.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right text-xs font-mono tabular-nums text-gray-500 dark:text-dark-300 sm:px-5">{aggregateBaseline === null ? "—" : Math.round(aggregateBaseline).toLocaleString()}</td>
                        <td className={`px-4 py-3 text-right text-xs font-mono tabular-nums sm:px-5 ${rowDelta !== null && rowDelta < -25 ? "font-bold text-red-600 dark:text-red-400" : "text-gray-500 dark:text-dark-300"}`}>{formatDelta(rowDelta)}</td>
                        <td className="px-4 py-3 text-right text-xs font-mono tabular-nums text-gray-500 dark:text-dark-300 sm:px-5">{row.categoriesExpected > 0 ? `${row.categoriesFound}/${row.categoriesExpected}` : "—"}</td>
                        <td className="px-4 py-3 text-right text-xs font-mono tabular-nums text-gray-500 dark:text-dark-300 sm:px-5">{row.storesReported}/{row.storesExpected}</td>
                        <td className="px-4 py-3 text-right sm:px-5"><StatusBadge status={row.status} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          {mode === "user" && !selectedStoreId && summaries.length > 1 && (
            <section className="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-gray-200 dark:bg-dark-800 dark:ring-dark-700">
              <div className="border-b border-gray-100 px-4 py-3.5 dark:border-dark-700 sm:px-5">
                <h3 className="text-sm font-bold text-gray-950 dark:text-white">Stores to review</h3>
                <p className="mt-0.5 text-[11px] text-gray-500 dark:text-dark-300">Issue is below 50% of normal or missing; Low is below 75%.</p>
              </div>
              <StoreSummaryTable summaries={summaries} />
            </section>
          )}
        </>
      ) : (
        <PanelEmptyState message={mode === "store" ? "Search for a store to view its production history." : "No production history is available for these stores."} />
      )}
    </div>
  );
}
